import { create } from 'zustand';
import { authFetch } from '@/config/api';
import { Channel, DirectMessageConversation, ChannelType } from '@/types/channel';
import { readResponseErrorMessage } from '@/lib/errorHandling';

interface ChannelsState {
  // State
  channels: Channel[];
  selectedChannelId: string | null;
  dmConversations: DirectMessageConversation[];
  selectedDMId: string | null;
  isLoading: boolean;
  isDMsLoading: boolean;
  error: string | null;

  // Computed/Getters
  getSelectedChannel: () => Channel | null;
  getSelectedDM: () => DirectMessageConversation | null;
  getChannelById: (id: string) => Channel | undefined;
  getDMById: (id: string) => DirectMessageConversation | undefined;
  getUnreadChannels: () => Channel[];
  getTotalUnreadCount: () => number;
  getChannelsByType: (type: ChannelType) => Channel[];

  // Actions - Channel Management
  loadChannels: () => Promise<void>;
  selectChannel: (channelId: string) => void;
  selectDM: (dmId: string) => void;
  clearSelection: () => void;
  joinChannel: (channelId: string) => Promise<void>;
  leaveChannel: (channelId: string) => Promise<void>;
  createChannel: (data: {
    name: string;
    description?: string;
    isPrivate: boolean;
    type: ChannelType;
  }) => Promise<Channel>;
  updateChannel: (channelId: string, updates: Partial<Channel>) => void;
  deleteChannel: (channelId: string) => void;

  // Actions - Unread Management
  incrementUnread: (channelId: string) => void;
  clearUnread: (channelId: string) => void;
  setUnreadCount: (channelId: string, count: number) => void;

  // Actions - DM Management
  loadDMs: () => Promise<void>;
  archiveDM: (dmId: string) => Promise<void>;
  createDM: (otherUserId: string, otherUsername: string) => Promise<DirectMessageConversation>;
  updateDM: (dmId: string, updates: Partial<DirectMessageConversation>) => void;
  updateDMByOtherUserId: (otherUserId: string, updates: Partial<DirectMessageConversation>) => void;
  removeDM: (dmId: string) => void;

  // Actions - Realtime Event Handlers
  handleChannelCreated: (channel: Channel) => void;
  handleChannelUpdated: (channel: Channel) => void;
  handleChannelDeleted: (channelId: string) => void;
  handleDMCreated: (dm: DirectMessageConversation) => void;

  // Cleanup
  reset: () => void;
}

export const useChannelsStore = create<ChannelsState>((set, get) => ({
  // Initial State
  channels: [],
  selectedChannelId: null,
  dmConversations: [],
  selectedDMId: null,
  isLoading: false,
  isDMsLoading: false,
  error: null,

  // Computed/Getters
  getSelectedChannel: () => {
    const { selectedChannelId, channels } = get();
    if (!selectedChannelId) return null;
    return channels.find(c => c.id === selectedChannelId) || null;
  },

  getSelectedDM: () => {
    const { selectedDMId, dmConversations } = get();
    if (!selectedDMId) return null;
    return dmConversations.find(dm => dm.id === selectedDMId) || null;
  },

  getChannelById: (id: string) => {
    return get().channels.find(c => c.id === id);
  },

  getDMById: (id: string) => {
    return get().dmConversations.find(dm => dm.id === id);
  },

  getUnreadChannels: () => {
    return get().channels.filter(c => (c.unreadCount ?? 0) > 0);
  },

  getTotalUnreadCount: () => {
    return get().channels.reduce((sum, c) => sum + (c.unreadCount ?? 0), 0);
  },

  getChannelsByType: (type: ChannelType) => {
    return get().channels.filter(c => c.type === type);
  },

  // Channel Management
  loadChannels: async () => {
    set({ isLoading: true, error: null });

    try {
      const response = await authFetch('/api/channels');
      if (!response.ok) {
        throw new Error('Failed to load channels');
      }

      const channels: Channel[] = await response.json();
      
      // Sort channels by name
      channels.sort((a, b) => a.name.localeCompare(b.name));

      set({ channels, isLoading: false });
    } catch (error) {
      console.error('Failed to load channels:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load channels',
        isLoading: false,
      });
    }
  },

  selectChannel: (channelId: string) => {
    set({ selectedChannelId: channelId, selectedDMId: null });
  },

  selectDM: (dmId: string) => {
    set({ selectedDMId: dmId, selectedChannelId: null });
  },

  clearSelection: () => {
    set({ selectedChannelId: null, selectedDMId: null });
  },

  joinChannel: async (channelId: string) => {
    try {
      const response = await authFetch(`/api/channels/${channelId}/join`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(await readResponseErrorMessage(response, 'Failed to join channel'));
      }

      // Optimistically update channels instead of full refetch
      set((state) => ({
        channels: state.channels.map(ch => 
          ch.id === channelId 
            ? { ...ch, isMember: true, memberCount: (ch.memberCount ?? 0) + 1 }
            : ch
        )
      }));
    } catch (error) {
      console.error('Failed to join channel:', error);
      throw error;
    }
  },

  leaveChannel: async (channelId: string) => {
    try {
      const response = await authFetch(`/api/channels/${channelId}/leave`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(await readResponseErrorMessage(response, 'Failed to leave channel'));
      }

      // Clear selection if leaving current channel
      if (get().selectedChannelId === channelId) {
        set({ selectedChannelId: null });
      }

      // Optimistically update channels instead of full refetch
      set((state) => ({
        channels: state.channels.map(ch => 
          ch.id === channelId 
            ? { ...ch, isMember: false, memberCount: Math.max(0, (ch.memberCount ?? 0) - 1) }
            : ch
        )
      }));
    } catch (error) {
      console.error('Failed to leave channel:', error);
      throw error;
    }
  },

  createChannel: async (data) => {
    try {
      const response = await authFetch('/api/channels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error('Failed to create channel');
      }

      const channel: Channel = await response.json();
      
      // Add new channel to the list
      set(state => {
        const channels = [...state.channels, channel];
        channels.sort((a, b) => a.name.localeCompare(b.name));
        return { channels };
      });

      return channel;
    } catch (error) {
      console.error('Failed to create channel:', error);
      throw error;
    }
  },

  updateChannel: (channelId: string, updates: Partial<Channel>) => {
    set(state => ({
      channels: state.channels.map(c =>
        c.id === channelId ? { ...c, ...updates } : c
      ),
    }));
  },

  deleteChannel: (channelId: string) => {
    set(state => ({
      channels: state.channels.filter(c => c.id !== channelId),
      selectedChannelId: state.selectedChannelId === channelId ? null : state.selectedChannelId,
    }));
  },

  // Unread Management
  incrementUnread: (channelId: string) => {
    set(state => ({
      channels: state.channels.map(c =>
        c.id === channelId ? { ...c, unreadCount: (c.unreadCount || 0) + 1 } : c
      ),
      dmConversations: state.dmConversations.map(dm =>
        dm.id === channelId ? { ...dm, unreadCount: (dm.unreadCount || 0) + 1 } : dm
      ),
    }));
  },

  clearUnread: (channelId: string) => {
    set(state => ({
      channels: state.channels.map(c =>
        c.id === channelId ? { ...c, unreadCount: 0 } : c
      ),
      dmConversations: state.dmConversations.map(dm =>
        dm.id === channelId ? { ...dm, unreadCount: 0 } : dm
      ),
    }));
  },

  setUnreadCount: (channelId: string, count: number) => {
    set(state => ({
      channels: state.channels.map(c =>
        c.id === channelId ? { ...c, unreadCount: count } : c
      ),
      dmConversations: state.dmConversations.map(dm =>
        dm.id === channelId ? { ...dm, unreadCount: count } : dm
      ),
    }));
  },

  // DM Management
  loadDMs: async () => {
    set({ isDMsLoading: true, error: null });

    try {
      const response = await authFetch('/api/dm');
      if (!response.ok) {
        throw new Error('Failed to load DM conversations');
      }

      const dmConversations: DirectMessageConversation[] = await response.json();
      
      // Sort by most recent message
      dmConversations.sort((a, b) => {
        const dateA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const dateB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        return dateB - dateA;
      });

      set(state => ({
        dmConversations: dmConversations.map(dm => ({
          ...dm,
          unreadCount: state.dmConversations.find(existing => existing.id === dm.id)?.unreadCount ?? dm.unreadCount ?? 0,
        })),
        isDMsLoading: false,
      }));
    } catch (error) {
      console.error('Failed to load DM conversations:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load DM conversations',
        isDMsLoading: false,
      });
    }
  },

  archiveDM: async (dmId: string) => {
    try {
      const response = await authFetch(`/api/dm/${dmId}/archive`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to archive DM');
      }

      // Remove from list
      set(state => ({
        dmConversations: state.dmConversations.filter(dm => dm.id !== dmId),
        selectedDMId: state.selectedDMId === dmId ? null : state.selectedDMId,
      }));
    } catch (error) {
      console.error('Failed to archive DM:', error);
      throw error;
    }
  },

  createDM: async (otherUserId: string, _otherUsername?: string) => {
    try {
      // Check if a DM with this user already exists in the store
      const existing = get().dmConversations.find(dm => dm.otherUserId === otherUserId);
      if (existing) {
        return existing;
      }

      const response = await authFetch(`/api/dm/with/${otherUserId}`, { method: 'POST' });

      if (!response.ok) {
        throw new Error('Failed to create DM conversation');
      }

      const dm: DirectMessageConversation = await response.json();
      
      // Only add if not already in the list (could have been added via realtime)
      set(state => {
        const alreadyExists = state.dmConversations.some(d => d.id === dm.id);
        if (alreadyExists) {
          return state;
        }
        return {
          dmConversations: [
            { ...dm, unreadCount: dm.unreadCount ?? 0 },
            ...state.dmConversations,
          ],
        };
      });

      return dm;
    } catch (error) {
      console.error('Failed to create DM conversation:', error);
      throw error;
    }
  },

  updateDM: (dmId: string, updates: Partial<DirectMessageConversation>) => {
    set(state => ({
      dmConversations: state.dmConversations.map(dm =>
        dm.id === dmId ? { ...dm, ...updates } : dm
      ),
    }));
  },

  updateDMByOtherUserId: (otherUserId: string, updates: Partial<DirectMessageConversation>) => {
    set(state => ({
      dmConversations: state.dmConversations.map(dm =>
        dm.otherUserId === otherUserId ? { ...dm, ...updates } : dm
      ),
    }));
  },

  removeDM: (dmId: string) => {
    set(state => ({
      dmConversations: state.dmConversations.filter(dm => dm.id !== dmId),
      selectedDMId: state.selectedDMId === dmId ? null : state.selectedDMId,
    }));
  },

  // Realtime Event Handlers
  handleChannelCreated: (channel: Channel) => {
    set(state => {
      const existing = state.channels.find(c => c.id === channel.id);
      if (existing) {
        const merged: Channel = {
          ...existing,
          ...channel,
          // Never downgrade membership (e.g., SignalR broadcast uses IsMember=false)
          isMember: (existing.isMember ?? false) || (channel.isMember ?? false),
        };

        const channels = state.channels.map(c => (c.id === channel.id ? merged : c));
        channels.sort((a, b) => a.name.localeCompare(b.name));
        return { channels };
      }

      const channels = [...state.channels, channel];
      channels.sort((a, b) => a.name.localeCompare(b.name));
      return { channels };
    });
  },

  handleChannelUpdated: (channel: Channel) => {
    set(state => {
      const existing = state.channels.find(c => c.id === channel.id);
      if (!existing) {
        return state;
      }

      const merged: Channel = {
        ...existing,
        ...channel,
        description: channel.description !== undefined ? channel.description : existing.description,
        sectionId: channel.sectionId !== undefined ? channel.sectionId : existing.sectionId,
        memberCount: channel.memberCount !== undefined ? channel.memberCount : existing.memberCount,
        unreadCount: channel.unreadCount !== undefined ? channel.unreadCount : existing.unreadCount,
        link1Url: channel.link1Url !== undefined ? channel.link1Url : existing.link1Url,
        link1Text: channel.link1Text !== undefined ? channel.link1Text : existing.link1Text,
        link1Icon: channel.link1Icon !== undefined ? channel.link1Icon : existing.link1Icon,
        link2Url: channel.link2Url !== undefined ? channel.link2Url : existing.link2Url,
        link2Text: channel.link2Text !== undefined ? channel.link2Text : existing.link2Text,
        link2Icon: channel.link2Icon !== undefined ? channel.link2Icon : existing.link2Icon,
        updatedAt: channel.updatedAt !== undefined ? channel.updatedAt : existing.updatedAt,
        // Never downgrade membership if we already know we're a member.
        isMember: (existing.isMember ?? false) || (channel.isMember ?? false),
      };

      return {
        channels: state.channels.map(c => (c.id === channel.id ? merged : c)),
      };
    });
  },

  handleChannelDeleted: (channelId: string) => {
    get().deleteChannel(channelId);
  },

  handleDMCreated: (dm: DirectMessageConversation) => {
    set(state => {
      // Check if already exists
      if (state.dmConversations.some(d => d.id === dm.id)) {
        return state;
      }

      return {
        dmConversations: [dm, ...state.dmConversations],
      };
    });
  },

  // Cleanup
  reset: () => {
    set({
      channels: [],
      selectedChannelId: null,
      dmConversations: [],
      selectedDMId: null,
      isLoading: false,
      isDMsLoading: false,
      error: null,
    });
  },
}));
